/**
 * «Как подключить» (MK42-6385): the curl examples of the guide, for the contour's base URL
 * (https://portal.mark-42.ru/api/v1 or https://portal.mark-42.co/api/v1). The key goes in an
 * environment variable, never into the command line history or a URL.
 */

export type Example = { id: string; title: string; note?: string; code: string };

const AUTH = '-H "Authorization: Bearer $M42_KEY"';

export function curlExamples(base: string): Example[] {
  return [
    {
      id: 'me',
      title: 'Проверить ключ',
      note: 'Ответ — компания, права ключа и контур. Право не нужно.',
      code: `export M42_KEY='m42_ru_live_…'   # ключ из «Интеграции», показывается один раз\ncurl -sS "${base}/me" ${AUTH}`,
    },
    {
      id: 'invoices',
      title: 'Счета для 1С',
      note: 'Право read:money (набор «Бухгалтерия»). Новые сверху, по 50 на страницу.',
      code: `curl -sS "${base}/invoices?limit=50" ${AUTH}`,
    },
    {
      id: 'next-page',
      title: 'Следующая страница',
      note: 'Передавайте nextCursor из ответа как cursor, пока он не станет null.',
      code: `curl -sS "${base}/invoices?limit=50&cursor=<nextCursor>" ${AUTH}`,
    },
    {
      id: 'invoice-pdf',
      title: 'PDF счёта',
      code: `curl -sS -o invoice.pdf "${base}/invoices/<invoiceId>/pdf" ${AUTH}`,
    },
    {
      id: 'acts',
      title: 'Акты и акт сверки',
      note: 'Акт сверки — только в российском контуре и ключом без привязки к проекту.',
      code: `curl -sS "${base}/acts?limit=50" ${AUTH}\ncurl -sS -o act.pdf "${base}/acts/<actId>/file" ${AUTH}\ncurl -sS "${base}/statement" ${AUTH}`,
    },
    {
      id: 'transfer',
      title: 'Отметить «оплачено переводом»',
      note: 'Право write:money. Это заявление, как кнопка в кабинете: счёт станет «processing», оплату подтвердит студия по выписке. Idempotency-Key обязателен: повтор с тем же ключом вернёт тот же ответ.',
      code: `curl -sS -X POST "${base}/invoices/<invoiceId>/transfer-reported" ${AUTH} \\\n  -H "Idempotency-Key: $(uuidgen)"`,
    },
    {
      id: 'projects',
      title: 'Проекты (id для идей)',
      note: 'Право read:projects — входит в набор «Идеи».',
      code: `curl -sS "${base}/projects" ${AUTH}`,
    },
    {
      id: 'initiative',
      title: 'Идея или запрос из CRM / n8n',
      note: 'Право write:initiatives. externalId — id сделки или задачи в вашей системе: повтор с тем же externalId вернёт уже созданную идею (200), а не вторую.',
      code: `curl -sS -X POST "${base}/initiatives" ${AUTH} \\\n  -H "Idempotency-Key: $(uuidgen)" \\\n  -H "Content-Type: application/json" \\\n  -d '{"projectId": "<projectId>", "title": "Форма заявки на странице доставки", "goal": "Больше заявок из рекламы", "urgency": "month", "externalId": "amo-48213", "externalUrl": "https://example.amocrm.ru/leads/detail/48213"}'`,
    },
    {
      id: 'initiatives-since',
      title: 'Что изменилось в идеях',
      note: 'updatedSince — время прошлой выборки (ISO 8601, UTC). Граничная секунда повторяется: обновляйте записи по id.',
      code: `curl -sS "${base}/initiatives?updatedSince=2026-10-01T00:00:00Z" ${AUTH}`,
    },
    {
      id: 'headers',
      title: 'Лимиты в заголовках ответа',
      note: 'RateLimit-Limit, RateLimit-Remaining, RateLimit-Reset (секунды до сброса окна).',
      code: `curl -sS -D - -o /dev/null "${base}/me" ${AUTH}`,
    },
  ];
}

/** n8n: the HTTP Request node, field by field. */
export const N8N_STEPS: string[] = [
  'Credentials → New → Header Auth: Name — Authorization, Value — Bearer m42_… (ключ с набором «Идеи»).',
  'Узел HTTP Request: Method — POST, URL — {base}/initiatives, Authentication — Generic → Header Auth (созданный выше).',
  'Send Headers: Idempotency-Key = {{ $execution.id }}-{{ $json.id }} (повтор того же запуска вернёт тот же ответ; дубль по сделке отсекает externalId).',
  'Send Body → JSON: projectId, title, goal, urgency, externalId = {{ $json.id }}, externalUrl — ссылка на сделку.',
  'Settings → Retry On Fail: включить; при 429 n8n повторит запрос — пауза не меньше Retry-After.',
];

/** 1С: the invoices into the books, the platform's own HTTP client. */
export function oneCExample(base: string): string {
  const u = new URL(base);
  return [
    'Ключ = "m42_ru_live_…"; // храните в защищённом хранилище настроек, не в коде',
    `Соединение = Новый HTTPСоединение("${u.host}", , , , , 60, Новый ЗащищенноеСоединениеOpenSSL);`,
    `Запрос = Новый HTTPЗапрос("${u.pathname}/invoices?limit=50");`,
    'Запрос.Заголовки.Вставить("Authorization", "Bearer " + Ключ);',
    'Ответ = Соединение.Получить(Запрос);',
    'Если Ответ.КодСостояния = 200 Тогда',
    '    Чтение = Новый ЧтениеJSON;',
    '    Чтение.УстановитьСтроку(Ответ.ПолучитьТелоКакСтроку("UTF-8"));',
    '    Страница = ПрочитатьJSON(Чтение); // Страница.data — счета, Страница.nextCursor — следующая',
    'ИначеЕсли Ответ.КодСостояния = 429 Тогда',
    '    Пауза = Число(Ответ.Заголовки.Получить("Retry-After")); // секунды до повтора',
    'КонецЕсли;',
  ].join('\n');
}

export const PROBLEM_EXAMPLE = `HTTP/1.1 403 Forbidden
Content-Type: application/problem+json

{
  "type": "about:blank",
  "title": "У ключа нет права write:money",
  "status": 403,
  "code": "scope_missing",
  "requestId": "6f1c…"
}`;

/** Stable `code` values and what to do; the title is for people, the code is for your program. */
export const ERROR_CODES: { status: number; code: string; what: string }[] = [
  { status: 400, code: 'idempotency_key_missing', what: 'POST без заголовка Idempotency-Key' },
  { status: 400, code: 'cursor_invalid', what: 'cursor не из прошлого ответа' },
  { status: 400, code: 'key_in_query', what: 'ключ в адресе — только в заголовке Authorization' },
  { status: 401, code: 'key_missing', what: 'нет заголовка Authorization: Bearer' },
  { status: 401, code: 'key_invalid', what: 'ключ не распознан' },
  { status: 401, code: 'key_wrong_contour', what: 'ключ другого контура: обращайтесь к адресу его контура' },
  { status: 401, code: 'key_expired', what: 'срок ключа истёк — перевыпустите в «Интеграции»' },
  { status: 401, code: 'key_revoked', what: 'ключ отозван' },
  { status: 403, code: 'scope_missing', what: 'у ключа нет нужного права' },
  { status: 403, code: 'api_disabled', what: 'API для компании выключено — напишите студии' },
  { status: 403, code: 'key_mode_mismatch', what: 'тестовый ключ — только для тестового клиента, и наоборот' },
  { status: 403, code: 'client_inactive', what: 'доступ компании к кабинету закрыт' },
  { status: 404, code: 'not_found', what: 'нет такого объекта или он вне прав ключа' },
  { status: 409, code: 'idempotency_conflict', what: 'Idempotency-Key уже использован с другим телом' },
  { status: 409, code: 'idempotency_in_progress', what: 'запрос с этим ключом ещё выполняется — повторите позже' },
  { status: 409, code: 'invoice_paid', what: 'счёт уже оплачен' },
  { status: 409, code: 'external_id_taken', what: 'externalId занят идеей другого проекта' },
  { status: 422, code: 'validation_failed', what: 'запрос не прошёл проверку; поля — в errors[]' },
  { status: 429, code: 'rate_limited', what: 'лимит ключа: повторите через Retry-After секунд' },
  { status: 502, code: 'upstream_unavailable', what: 'сервис-источник не ответил — повторите через минуту' },
];

// --- Вебхуки (MK42-6388) --------------------------------------------------------------------------

/** What the receiver gets: a «thin» event — ids, numbers, statuses; the rest is read with the API key. */
export function webhookPayloadExample(base: string): string {
  const host = base.replace(/\/api\/v1$/, '');
  return `POST /hooks/mark42 HTTP/1.1
Content-Type: application/json
User-Agent: Mark-42-Webhooks/1
webhook-id: evt_01JA9X3K7Q2M8V4T6R0B5N1C2D
webhook-timestamp: 1760464800
webhook-signature: v1,<base64 HMAC-SHA256>

{
  "id": "evt_01JA9X3K7Q2M8V4T6R0B5N1C2D",
  "type": "invoice.issued",
  "createdAt": "2026-10-14T18:00:00Z",
  "contour": "ru",
  "clientId": "9fd470989d29",
  "data": { "invoiceId": "inv_3b1f…", "number": "0004", "projectId": "73d54cbfee86" },
  "links": { "self": "${base}/invoices/inv_3b1f…", "web": "${host}/money/inv_3b1f…" }
}`;
}

export const WEBHOOK_EVENTS: { type: string; what: string; scope: string }[] = [
  { type: 'invoice.issued', what: 'Выставлен счёт: data.invoiceId, number, projectId', scope: 'read:money' },
  { type: 'report.published', what: 'Опубликован отчёт: data.reportId, projectId', scope: 'read:reports' },
  { type: 'task.needs_client', what: 'Задача ждёт вас: data.taskId, projectId', scope: 'read:tasks' },
  { type: 'test.ping', what: 'Проверка: по кнопке «Проверить», приходит всем точкам', scope: '—' },
];

export const VERIFY_PYTHON = `import base64, hashlib, hmac, time

SECRET = "whsec_…"  # из «Интеграции», показывается один раз

def verify(headers: dict, body: bytes) -> bool:
    msg_id = headers["webhook-id"]
    ts = headers["webhook-timestamp"]
    if abs(time.time() - int(ts)) > 300:      # старше 5 минут — отвергнуть
        return False
    key = base64.b64decode(SECRET.removeprefix("whsec_"))
    signed = f"{msg_id}.{ts}.".encode() + body  # тело — как пришло, байт в байт
    expected = base64.b64encode(hmac.new(key, signed, hashlib.sha256).digest()).decode()
    # подписей может быть две (после смены секрета): "v1,<new> v1,<old>"
    return any(
        hmac.compare_digest(sig.split(",", 1)[1], expected)
        for sig in headers["webhook-signature"].split(" ")
        if sig.startswith("v1,")
    )

# Или библиотека: pip install standardwebhooks
# from standardwebhooks import Webhook; Webhook(SECRET).verify(body, headers)`;

export const VERIFY_JS = `import crypto from "node:crypto";

const SECRET = process.env.M42_WEBHOOK_SECRET; // "whsec_…"

export function verify(headers, rawBody /* Buffer, как пришло */) {
  const id = headers["webhook-id"];
  const ts = headers["webhook-timestamp"];
  if (Math.abs(Date.now() / 1000 - Number(ts)) > 300) return false;
  const key = Buffer.from(SECRET.replace(/^whsec_/, ""), "base64");
  const expected = crypto
    .createHmac("sha256", key)
    .update(\`\${id}.\${ts}.\`)
    .update(rawBody)
    .digest("base64");
  return headers["webhook-signature"]
    .split(" ")
    .filter((s) => s.startsWith("v1,"))
    .some((s) => {
      const got = Buffer.from(s.slice(3));
      const want = Buffer.from(expected);
      return got.length === want.length && crypto.timingSafeEqual(got, want);
    });
}

// Или библиотека: npm i standardwebhooks — new Webhook(SECRET).verify(rawBody, headers)`;

/** PHP for a site, and the same steps for 1С (HTTP-сервис), where HMAC is assembled from ХешированиеДанных. */
export const VERIFY_PHP_1C = `<?php
$secret = base64_decode(substr(getenv('M42_WEBHOOK_SECRET'), 6)); // без "whsec_"
$body = file_get_contents('php://input');
$id = $_SERVER['HTTP_WEBHOOK_ID'];
$ts = $_SERVER['HTTP_WEBHOOK_TIMESTAMP'];
if (abs(time() - (int)$ts) > 300) { http_response_code(400); exit; }
$expected = base64_encode(hash_hmac('sha256', "$id.$ts.$body", $secret, true));
$ok = false;
foreach (explode(' ', $_SERVER['HTTP_WEBHOOK_SIGNATURE']) as $sig) {
    if (str_starts_with($sig, 'v1,') && hash_equals($expected, substr($sig, 3))) $ok = true;
}
if (!$ok) { http_response_code(401); exit; }
http_response_code(204); // быстро ответить 2xx, разбирать событие — потом

// 1С (HTTP-сервис), те же шаги:
// Ключ    = Base64Значение(Сред(Секрет, 7));
// Строка  = ИдСобытия + "." + Время + "." + ТелоКакПришло;
// Подпись = Base64Строка(HMAC_SHA256(Ключ, Строка)); // HMAC из ХешированиеДанных(SHA256):
//   SHA256((Ключ XOR opad) + SHA256((Ключ XOR ipad) + Строка)), блок 64 байта
// Сравнить с каждой "v1,…" из webhook-signature; событие старше 5 минут — отвергнуть.`;

export const WEBHOOK_RETRIES = '30 с → 2 мин → 10 мин → 1 ч → 6 ч → 24 ч';
